import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta, shortDate } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import { EmptyState } from "@/components/EmptyState";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import VoiceRecorderButton from "@/components/VoiceRecorderButton";
import { toast } from "sonner";
import {
  ChevronLeft, BookOpen, Trash2, Sparkles, Plus, Mic, Check, ChevronDown, ChevronUp,
  Leaf, Users, Loader2,
} from "lucide-react";

const MOODS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const TAG_FIELDS = [
  ["emotions", "eva_tag_emotions"], ["beliefs", "eva_tag_beliefs"], ["behaviors", "eva_tag_behaviors"],
  ["body_sensations", "eva_tag_body"], ["situations", "eva_tag_situations"], ["themes", "eva_tag_themes"],
];

function TagLine({ label, items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div className="mt-1.5">
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it, i) => (
          <span key={i} className="rounded-full border border-border bg-secondary/40 px-2 py-0.5 text-[11px] text-foreground">{typeof it === "string" ? it : it?.label}</span>
        ))}
      </div>
    </div>
  );
}

function TagBlock({ tags, t }) {
  if (!tags || (!tags.summary && !TAG_FIELDS.some(([f]) => Array.isArray(tags[f]) && tags[f].length))) return null;
  return (
    <div className="mt-2 space-y-1">
      {tags.summary ? <p className="text-sm italic text-muted-foreground">💚 {tags.summary}</p> : null}
      {TAG_FIELDS.map(([f, k]) => <TagLine key={f} label={t(k)} items={tags[f]} />)}
      {tags.gentle_reflection ? (
        <div className="mt-2 rounded-xl border border-border bg-secondary/30 p-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-primary">{t("eva_reflection")}</p>
          <p className="mt-0.5 text-sm text-foreground">{tags.gentle_reflection}</p>
        </div>
      ) : null}
    </div>
  );
}

export default function Journal() {
  const { t, lang } = useI18n();
  usePageMeta(t("jr.title"));
  const navigate = useNavigate();

  const [tab, setTab] = useState("entries");
  const [features, setFeatures] = useState(null);
  const [entries, setEntries] = useState(null);
  const [latestDigest, setLatestDigest] = useState(null);
  const [wevaHistory, setWevaHistory] = useState([]);

  const [showAdd, setShowAdd] = useState(false);
  const [text, setText] = useState("");
  const [mood, setMood] = useState(null);
  const [weva, setWeva] = useState(true);
  const [saving, setSaving] = useState(false);

  const [openEntry, setOpenEntry] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [generating, setGenerating] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    const [f, list, latest, hist] = await Promise.all([
      api.get("/journal/features").then((r) => r.data).catch(() => null),
      api.get("/journal?limit=100").then((r) => r.data).catch(() => []),
      api.get("/journal/analyze/latest").then((r) => r.data).catch(() => null),
      api.get("/journal/analyze/history").then((r) => r.data).catch(() => []),
    ]);
    setFeatures(f);
    setEntries(Array.isArray(list) ? list : []);
    setLatestDigest(latest && latest.id ? latest : null);
    setWevaHistory(Array.isArray(hist) ? hist : []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const voiceLocked = features ? !features.voice : false;
  const usedTxt = features ? t("journal_monthly_used", { n: features.used_this_month ?? 0, q: (features.monthly_quota ?? 0) >= 9999 ? "∞" : (features.monthly_quota ?? 0) }) : "";

  const submit = async () => {
    const body = text.trim();
    if (!body) { toast.error(t("jr.empty_err")); return; }
    setSaving(true);
    try {
      await api.post("/journal", { text: body, mood, weva_analysis_enabled: weva, auto_analyze: false, lang });
      setText(""); setMood(null); setWeva(true); setShowAdd(false);
      await load();
      toast.success(t("jr.saved"));
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : apiError(e, t("jr.save_failed")));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("jr.delete_confirm"))) return;
    try {
      await api.delete(`/journal/${id}`);
      setEntries((prev) => prev.filter((x) => x.id !== id));
      setOpenEntry(null);
      toast.success(t("jr.deleted"));
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleSelected = (id) => setSelectedIds((prev) => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });

  const generate = async () => {
    if (generating || (entries || []).length === 0) return;
    setGenerating(true);
    try {
      const ids = Array.from(selectedIds);
      await api.post("/journal/analyze", { range_days: 30, lang, entry_ids: ids.length > 0 ? ids : undefined });
      setSelectedIds(new Set());
      await load();
      toast.success(t("jr.saved"));
    } catch (e) {
      if (e?.response?.status === 402) {
        if (window.confirm(t("weva_credits_needed"))) navigate("/app/credits");
      } else if (e?.response?.status === 400) {
        toast.error(t("journal_pick_empty"));
      } else { toast.error(apiError(e)); }
    } finally { setGenerating(false); }
  };

  const digestText = (h) => h.analysis?.narrative || (Array.isArray(h.analysis?.highlights) ? h.analysis.highlights.join("\n\n") : "") || h.analysis?.summary || t("journal_weva_report_fallback");

  return (
    <div className="space-y-5" data-testid="journal-screen">
      <button onClick={() => navigate("/app/deep-within")} data-testid="jr-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> {t("dw.title")}
      </button>

      <div className="flex items-start justify-between gap-3">
        <PageHeader eyebrow={t("eva")} title={t("jr.title")} subtitle={t("journal_subtitle")} />
        <Button onClick={() => { setShowAdd(true); }} data-testid="journal-add-btn" className="shrink-0 gap-1.5 rounded-full">
          <Plus className="h-4 w-4" /> {t("journal_new_entry")}
        </Button>
      </div>
      {usedTxt ? <p className="-mt-2 text-xs font-medium text-primary" data-testid="jr-quota">{usedTxt}</p> : null}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="entries" data-testid="journal-tab-entries"><BookOpen className="mr-1.5 h-3.5 w-3.5" />{t("journal_tab_entries")}</TabsTrigger>
          <TabsTrigger value="weva" data-testid="journal-tab-weva"><Sparkles className="mr-1.5 h-3.5 w-3.5" />{t("journal_tab_weva")}</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "entries" ? (
        <div className="space-y-3">
          {latestDigest?.analysis?.summary ? (
            <button onClick={() => setTab("weva")} data-testid="latest-digest-card" className="w-full rounded-2xl border border-primary/40 bg-primary/5 p-4 text-left">
              <div className="mb-1 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-[11px] font-semibold uppercase tracking-wide text-primary">{t("eva_latest_insight")}</span>
                {latestDigest.auto ? <span className="rounded bg-primary px-1.5 py-0.5 text-[9px] font-bold text-primary-foreground">AUTO</span> : null}
              </div>
              <p className="line-clamp-3 text-sm text-foreground">{latestDigest.analysis.summary}</p>
              <p className="mt-1 text-xs text-muted-foreground">{shortDate(latestDigest.created_at)}</p>
            </button>
          ) : null}

          {entries === null ? (
            <Spinner label={t("common.loading")} />
          ) : entries.length === 0 ? (
            <EmptyState icon={BookOpen} title={t("journal_empty")} />
          ) : (
            <div className="space-y-3" data-testid="jr-list">
              {entries.map((e) => (
                <div key={e.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`jr-entry-${e.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <button onClick={() => setOpenEntry(e)} className="min-w-0 flex-1 text-left" data-testid={`journal-card-${e.id}`}>
                      <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span>{shortDate(e.created_at)}</span>
                        {e.has_audio ? <Mic className="h-3 w-3 text-primary" /> : null}
                        {typeof e.mood === "number" ? <span className="rounded-full bg-secondary/60 px-2 py-0.5">{t("jr.mood")}: {e.mood}/10</span> : null}
                        {e.weva_analysis_enabled ? <span className="inline-flex items-center gap-1 text-primary"><Sparkles className="h-3 w-3" /> WEVA</span> : null}
                      </div>
                      <p className="line-clamp-3 whitespace-pre-line text-sm text-foreground">{e.text}</p>
                      <TagBlock tags={e.tags} t={t} />
                    </button>
                    <button onClick={() => remove(e.id)} data-testid={`jr-delete-${e.id}`} className="shrink-0 rounded-lg p-2 text-muted-foreground transition-colors hover:bg-rose-50 hover:text-rose-600" title={t("jr.delete")}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3" data-testid="journal-weva-tab">
          <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4">
            <p className="text-sm text-foreground">{features && features.tier && features.tier !== "free" ? t("journal_weva_included", { n: 1, reportWord: t("journal_weva_report_singular"), plan: (features.tier || "").replace(/_/g, " ") }) : t("journal_weva_credits_hint")}</p>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-sm italic text-muted-foreground">
              {selectedIds.size === 0 ? t("journal_pick_prompt") : (selectedIds.size === 1 ? t("journal_pick_selected_singular", { n: 1 }) : t("journal_pick_selected_plural", { n: selectedIds.size }))}
            </p>
            <button onClick={() => setSelectedIds(new Set())} disabled={selectedIds.size === 0} data-testid="weva-clear-selection" className="text-xs font-medium text-primary hover:underline disabled:opacity-0">{t("journal_pick_clear")}</button>
          </div>

          {(entries || []).length === 0 ? (
            <p className="text-sm italic text-muted-foreground">{t("journal_pick_empty")}</p>
          ) : (
            <div className="space-y-2">
              {(entries || []).map((e) => {
                const picked = selectedIds.has(e.id);
                return (
                  <button key={`pick-${e.id}`} onClick={() => toggleSelected(e.id)} data-testid={`weva-pick-${e.id}`}
                    className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${picked ? "border-primary bg-primary/10" : "border-border bg-card hover:bg-accent/30"}`}>
                    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${picked ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>
                      {picked ? <Check className="h-3.5 w-3.5" /> : null}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] text-muted-foreground">{shortDate(e.created_at)}</p>
                      <p className="line-clamp-2 text-sm text-foreground">{e.text}</p>
                    </div>
                    {typeof e.mood === "number" ? <span className="rounded-full bg-secondary/60 px-2 py-0.5 text-[11px] text-muted-foreground">{e.mood}</span> : null}
                  </button>
                );
              })}
            </div>
          )}

          <Button onClick={generate} disabled={generating || (entries || []).length === 0} data-testid="weva-generate" className="w-full gap-2 rounded-full">
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {selectedIds.size > 0 ? t("journal_weva_btn_picked", { n: selectedIds.size }) : t("journal_weva_btn_30d")}
          </Button>
          <p className="text-xs italic text-muted-foreground">{selectedIds.size > 0 ? t("journal_weva_hint_picked") : t("journal_weva_hint_30d")}</p>

          <h2 className="pt-2 font-display text-lg text-foreground">{t("journal_tab_weva")}</h2>
          {wevaHistory.length === 0 ? (
            <p className="text-sm italic text-muted-foreground">{t("journal_weva_history_empty")}</p>
          ) : wevaHistory.map((h) => {
            const isOpen = expandedId === h.id;
            return (
              <div key={h.id} className="overflow-hidden rounded-2xl border border-border bg-card" data-testid={`weva-report-${h.id}`}>
                <button onClick={() => setExpandedId(isOpen ? null : h.id)} className="flex w-full items-center gap-3 p-4 text-left">
                  <Sparkles className="h-4 w-4 shrink-0 text-primary" />
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm text-foreground ${isOpen ? "" : "line-clamp-3"}`}>{isOpen ? digestText(h) : (h.analysis?.summary || t("journal_weva_report_fallback"))}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{shortDate(h.created_at)}{h.selected_scope ? ` · ${t("picked")}` : ""}</p>
                  </div>
                  {isOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Compose dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-lg" data-testid="journal-compose-dialog">
          <DialogHeader><DialogTitle>{t("journal_prompt")}</DialogTitle></DialogHeader>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={t("journal_placeholder")} className="min-h-[140px]" maxLength={8000} data-testid="jr-text" />
          <div className="flex items-center gap-2">
            <VoiceRecorderButton onTranscribed={(txt) => setText((p) => (p ? p + "\n" : "") + txt)} lang={lang} locked={voiceLocked} onLockedClick={() => navigate("/app/membership")} />
            <span className="text-xs text-muted-foreground">{t("jr_voice_hint")}</span>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{t("jr.mood")}</span>
              <div className="flex flex-wrap gap-1">
                {MOODS.map((m) => (
                  <button key={m} onClick={() => setMood(mood === m ? null : m)} data-testid={`jr-mood-${m}`}
                    className={`h-7 w-7 rounded-full text-xs font-medium transition-colors ${mood === m ? "bg-primary text-primary-foreground" : "bg-secondary/60 text-muted-foreground hover:bg-accent"}`}>{m}</button>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <Switch checked={weva} onCheckedChange={setWeva} data-testid="jr-weva" />
              <Sparkles className="h-3.5 w-3.5 text-primary" /> {t("jr.weva")}
            </label>
          </div>
          <div className="flex justify-end">
            <Button onClick={submit} disabled={saving || !text.trim()} className="rounded-full" data-testid="jr-save">
              {saving ? t("common.saving") : t("journal_save")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Entry detail dialog */}
      <Dialog open={!!openEntry} onOpenChange={(o) => !o && setOpenEntry(null)}>
        <DialogContent className="max-w-lg" data-testid="journal-detail-dialog">
          {openEntry ? (
            <>
              <DialogHeader>
                <DialogTitle>{t("journal_entry_detail_title")}</DialogTitle>
                <p className="text-xs text-muted-foreground">{shortDate(openEntry.created_at)}{typeof openEntry.mood === "number" ? ` · ${t("jr.mood")} ${openEntry.mood}/10` : ""}</p>
              </DialogHeader>
              <div className="max-h-[50vh] overflow-y-auto">
                <p className="whitespace-pre-line text-sm leading-relaxed text-foreground">{openEntry.text}</p>
                <TagBlock tags={openEntry.tags} t={t} />
              </div>
              <p className="pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">{t("journal_take_this_further")}</p>
              <div className="space-y-2">
                <button onClick={() => { setSelectedIds(new Set([openEntry.id])); setTab("weva"); setOpenEntry(null); }} data-testid="journal-detail-work-alone"
                  className="flex w-full items-center gap-3 rounded-xl border border-primary/40 bg-card p-3 text-left transition-colors hover:bg-primary/5">
                  <Leaf className="h-5 w-5 shrink-0 text-primary" />
                  <div className="min-w-0 flex-1"><p className="text-sm font-medium text-foreground">{t("journal_work_alone_title")}</p><p className="text-xs text-muted-foreground">{t("journal_work_alone_sub")}</p></div>
                </button>
                <button onClick={() => { setOpenEntry(null); navigate("/app/coach-hub"); }} data-testid="journal-detail-work-coach"
                  className="flex w-full items-center gap-3 rounded-xl border border-primary/40 bg-card p-3 text-left transition-colors hover:bg-primary/5">
                  <Users className="h-5 w-5 shrink-0 text-primary" />
                  <div className="min-w-0 flex-1"><p className="text-sm font-medium text-foreground">{t("journal_work_coach_title")}</p><p className="text-xs text-muted-foreground">{t("journal_work_coach_sub")}</p></div>
                </button>
              </div>
              <div className="flex justify-end">
                <button onClick={() => remove(openEntry.id)} className="inline-flex items-center gap-1 text-xs text-rose-600 hover:underline"><Trash2 className="h-3.5 w-3.5" /> {t("jr.delete")}</button>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
